/**
 * VideoPublishButton - Publish/Share button for video workspace
 *
 * Features:
 * - Multiple published links with per-link management
 * - "Update to Latest" refreshes an existing link's versions (same URL)
 * - "New Link" creates a new link while keeping old ones
 * - Per-link: copy, open, embed, revoke
 * - Dropdown with export to MP4 / PDF options
 * - MP4 / PDF export progress shown inline in dropdown (non-blocking)
 * - Loading animation on button while export is in progress
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import {
  Globe,
  Copy,
  Check,
  Eye,
  Code,
  Zap,
  Trash2,
  ChevronDown,
  Loader2,
  ExternalLink,
  Film,
  FileText,
  CheckCircle2,
  XCircle,
  SkipForward,
  AlertTriangle,
  RefreshCw,
  RotateCcw,
  Plus,
  Link2,
  Lock,
  BarChart3,
} from 'lucide-react';
import { toast } from 'sonner';
import api, { videoShareAPI, videoExportAPI, type VideoShareLinkInfo } from '@/services/api';
import { useVideoExportAll, exportToastFor, type SlideExportStatus, type ExportAllPhase } from '@/hooks/useVideoExportAll';
import { DEFAULT_SLIDE_RESOLUTION } from '@/config/gsapViewer';
import { useVideoPdfExport, type PdfExportPhase } from '@/hooks/useVideoPdfExport';
import { useExportDiff } from '@/hooks/useExportDiff';
import { ExportChangeBadge } from './ExportChangeBadge';
import { ReviewerAccessRow } from './ReviewerAccessRow';
import { ViewerLinkRow } from './ViewerLinkRow';
import { BlogShareSection } from './blog';
import { useHasBlog } from '@/hooks/useHasBlog';
import { cn } from '@/lib/utils';
import { studioBOn } from '@/lib/studioB/flag';
import { announceLearnTrigger } from '@/stores/embeddedIvyChatStore';
import { useUpgradeModal } from '@/stores/upgradeModalStore';
import { Download } from 'lucide-react';
import {
  ensureNotificationPermission,
  playCompletionChime,
  showExportCompleteNotification,
} from '@/lib/exportNotifications';

interface SlideInfo {
  id: string;
  title: string;
}

interface VideoPublishButtonProps {
  conversationId: string;
  /** Total number of slides in the video */
  slideCount?: number;
  /** Whether all slides are generated */
  allSlidesReady?: boolean;
  /** Slide metadata for export progress display */
  slides?: SlideInfo[];
  /** Compact mode for smaller spaces */
  compact?: boolean;
  className?: string;
  /** Project resolution for embed code */
  resolution?: { width: number; height: number };
  /** Initial active tab inside the Share dropdown. Auto-set from
   *  the workspace's active view by the parent. */
  defaultShareTab?: 'video' | 'blog';
}

// Covers this dropdown too, not just the workspace tab. Gating `hasBlog` is the one choke
// point: it drops the strip AND both blog sections, and forces the video branch.
const SHOW_BLOG_TAB = import.meta.env.VITE_SHOW_BLOG_TAB === 'true';

/**
 * Tab strip rendered at the top of the Share dropdown when a blog
 * companion exists. Lets users flip between video and blog actions
 * without scrolling a tall stacked menu.
 *
 * Click-only navigation — no keyboard shortcuts (the surrounding
 * DropdownMenu owns arrow keys, and we don't want tabs to compete).
 */
const ShareTabStrip: React.FC<{
  activeTab: 'video' | 'blog';
  onChange: (tab: 'video' | 'blog') => void;
}> = ({ activeTab, onChange }) => (
  <div className="flex items-center gap-0.5 p-1 m-1 rounded-md bg-muted/40">
    {(['video', 'blog'] as const).map((t) => (
      <button
        key={t}
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onChange(t);
        }}
        className={cn(
          'flex-1 px-2.5 py-1 text-xs font-medium rounded transition-all',
          activeTab === t
            ? 'bg-background text-foreground shadow-sm'
            : 'text-muted-foreground hover:text-foreground',
        )}
      >
        {t === 'video' ? 'Video' : 'Blog'}
      </button>
    ))}
  </div>
);

/** Direction B (beta only): Publish's menu and dialog are B surfaces (the panel map applies), its first action the ink button. */
const B_MENU = 'w-80 rounded-[var(--b-radius-card)] border-b-line bg-b-elevated p-1.5';
const B_PRIMARY = 'mb-1 justify-center rounded-[var(--b-radius-control)] bg-b-ink py-2 text-b-control text-b-on-ink focus:bg-b-ink focus:text-b-on-ink focus:opacity-90';
const B_DIALOG = 'rounded-[var(--b-radius-sheet)] border-b-line bg-b-elevated';

export const VideoPublishButton: React.FC<VideoPublishButtonProps> = ({
  conversationId,
  slideCount = 0,
  allSlidesReady = false,
  slides: slidesInfo = [],
  compact = false,
  className,
  resolution = DEFAULT_SLIDE_RESOLUTION,
  defaultShareTab = 'video',
}) => {
  // Has a blog companion? Determines whether to show the tab strip.
  // Forced false while the blog UI is hidden, so the whole dropdown stays video-only.
  const { exists: blogExists } = useHasBlog(conversationId);
  const hasBlog = SHOW_BLOG_TAB && blogExists;
  // Active tab inside the dropdown. Resets to the requested default
  // each time the workspace tab changes (parent re-renders).
  const [shareTab, setShareTab] = useState<'video' | 'blog'>(defaultShareTab);
  useEffect(() => {
    setShareTab(defaultShareTab);
  }, [defaultShareTab]);
  const [isPublished, setIsPublished] = useState(false);
  const [shareLinks, setShareLinks] = useState<VideoShareLinkInfo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isPublishing, setIsPublishing] = useState(false);
  const [updatingLinkId, setUpdatingLinkId] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null); // "share-{id}" or "embed-{id}"
  const [showUnpublishDialog, setShowUnpublishDialog] = useState(false);
  // Direction B: the menu also opens from outside (the reopen summary's Make an MP4), for this video only.
  const b = studioBOn();
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => {
    if (!b) return;
    const onOpen = (e: Event) => { if ((e as CustomEvent).detail?.conversationId === conversationId) setMenuOpen(true); };
    window.addEventListener('studio-open-publish', onOpen);
    return () => window.removeEventListener('studio-open-publish', onOpen);
  }, [b, conversationId]);
  const [unpublishTargetId, setUnpublishTargetId] = useState<string | null>(null);
  const [unpublishingId, setUnpublishingId] = useState<string | null>(null);
  // Watermark: entitlement-gated. Fetch plan once; only paid (credits) users can
  // toggle the option off. The server enforces this independently — the UI
  // state is advisory.
  const [canRemoveWatermark, setCanRemoveWatermark] = useState<boolean>(false);
  const [removeWatermark, setRemoveWatermark] = useState<boolean>(false);
  // 4K export: entitlement-gated to paid (credits) accounts. Server
  // hard-fails 4K requests from non-entitled users (402 + FOUR_K_NOT_ALLOWED)
  // rather than silently downgrading — the UI value is advisory but selecting
  // a locked option is prevented client-side too.
  const [can4k, setCan4k] = useState<boolean>(false);
  const [outputScale, setOutputScale] = useState<'1080p' | '4k'>('1080p');

  const exportAll = useVideoExportAll(conversationId);
  const pdfExport = useVideoPdfExport(conversationId);
  const exportDiff = useExportDiff(conversationId);
  const prevPhaseRef = useRef<ExportAllPhase>('idle');
  const prevPdfPhaseRef = useRef<PdfExportPhase>('idle');

  // Toast on export completion/failure (fires once per transition).
  // Also fires a browser notification + audio chime on the terminal "done"
  // transition so users who've backgrounded the tab still get notified.
  useEffect(() => {
    const prev = prevPhaseRef.current;
    prevPhaseRef.current = exportAll.phase;

    const note = exportToastFor(prev, exportAll.phase);
    if (!note) return;
    toast[note.level](note.message);
    if (note.level === 'success') {
      playCompletionChime();
      showExportCompleteNotification({
        title: 'Video export complete',
        body: 'Your MP4 is ready to download.',
        tag: 'ivy-mp4-export',
      });
      // Successful export establishes a new baseline — refetch the diff so the
      // change badge clears.
      void exportDiff.refetch();
    }
  }, [exportAll.phase]);

  // Toast + notification on PDF export completion/failure
  useEffect(() => {
    const prev = prevPdfPhaseRef.current;
    prevPdfPhaseRef.current = pdfExport.phase;

    if ((prev === 'exporting' || prev === 'submitting') && pdfExport.phase === 'completed') {
      toast.success('PDF export complete! Click "Download PDF" to save.');
      playCompletionChime();
      showExportCompleteNotification({
        title: 'PDF export complete',
        body: 'Your PDF deck is ready to download.',
        tag: 'ivy-pdf-export',
      });
    } else if ((prev === 'exporting' || prev === 'submitting') && pdfExport.phase === 'failed') {
      toast.error(pdfExport.error || 'PDF export failed');
    }
  }, [pdfExport.phase, pdfExport.error]);

  // Fetch publish status on mount
  const fetchPublishStatus = useCallback(async () => {
    try {
      setIsLoading(true);
      const status = await videoShareAPI.getPublishStatus(conversationId);
      setIsPublished(status.isPublished);
      setShareLinks(status.shareLinks);
    } catch (error) {
      console.error('Failed to fetch publish status:', error);
    } finally {
      setIsLoading(false);
    }
  }, [conversationId]);

  useEffect(() => {
    fetchPublishStatus();
  }, [fetchPublishStatus]);

  // Fetch entitlement once per mount to decide whether the watermark toggle
  // is enabled. Silent on failure — toggle simply stays in its locked state.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get('/billing/entitlement');
        if (!cancelled) {
          setCanRemoveWatermark(!!r.data?.canExportWithoutWatermark);
          setCan4k(!!r.data?.can4k);
        }
      } catch {
        /* best-effort */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Get active links
  const activeLinks = shareLinks.filter(
    l => l.isActive && (!l.expiresAt || new Date(l.expiresAt) > new Date())
  );

  // Publish video (create new link)
  const handlePublish = async () => {
    if (!allSlidesReady) {
      toast.warning('Please wait for all slides to be generated before publishing');
      return;
    }

    try {
      setIsPublishing(true);
      const result = await videoShareAPI.publish(conversationId);

      if (result.success) {
        announceLearnTrigger(conversationId);
        toast.success('Video published successfully!');
        setIsPublished(true);
        setShareLinks([result.shareLink, ...shareLinks]);

        // Clipboard write fails when the document isn't focused — never let it
        // surface as a publish failure.
        try {
          await navigator.clipboard.writeText(result.shareLink.shareUrl);
          setCopied(`share-${result.shareLink.id}`);
          setTimeout(() => setCopied(null), 2000);
        } catch {
          toast.info('Link ready — use the copy button to copy it');
        }
      }
    } catch (error: any) {
      console.error('Failed to publish video:', error);
      toast.error(error.message || 'Failed to publish video');
    } finally {
      setIsPublishing(false);
    }
  };

  // Update an existing link to latest versions
  // Toggle whether reviewers can leave comments via ?review=true on a given share link.
  // We update server-side, then patch local state with the returned link record so the
  // UI flips without a full refetch.
  const handleToggleAllowReviews = async (linkId: string, next: boolean) => {
    try {
      const result = await videoShareAPI.setAllowReviews(conversationId, linkId, next);
      if (result.success) {
        setShareLinks(prev => prev.map(l => l.id === linkId ? result.shareLink : l));
        toast.success(next ? 'Reviews enabled — share the review link' : 'Reviews disabled');
      }
    } catch (error: any) {
      console.error('Failed to toggle allowReviews:', error);
      toast.error(error?.response?.data?.message || error?.message || 'Failed to update share settings');
    }
  };

  const handleUpdateLink = async (linkId: string) => {
    try {
      setUpdatingLinkId(linkId);
      const result = await videoShareAPI.update(conversationId, linkId);

      if (result.success) {
        announceLearnTrigger(conversationId);
        toast.success('Link updated to latest slide versions!');
        // Replace the updated link in state
        setShareLinks(prev =>
          prev.map(l => l.id === linkId ? result.shareLink : l)
        );
      }
    } catch (error: any) {
      console.error('Failed to update share link:', error);
      toast.error(error.message || 'Failed to update link');
    } finally {
      setUpdatingLinkId(null);
    }
  };

  // Start MP4 export
  const handleExportMp4 = async () => {
    if (!allSlidesReady) {
      toast.warning('Please wait for all slides to be generated before exporting');
      return;
    }

    // Ask for notification permission opportunistically — the click is the
    // user gesture browsers require. Fire-and-forget; the prompt is one-shot
    // per origin, so repeat calls after the first answer are no-ops.
    ensureNotificationPermission();

    try {
      await exportAll.startExportAll(conversationId, {
        removeWatermark: canRemoveWatermark && removeWatermark,
        // Coerce 4K back to 1080p client-side too if the user somehow has
        // outputScale='4k' selected without entitlement (state desync) —
        // the server hard-fails this anyway, but cheaper to never send.
        outputScale: can4k && outputScale === '4k' ? '4k' : '1080p',
      });
    } catch (error: any) {
      console.error('Failed to start MP4 export:', error);
      const serverMsg = error?.response?.data?.error;
      const code = error?.response?.data?.code;
      if (code === 'FOUR_K_NOT_ALLOWED') {
        // Entitlement drift since page load — re-fetch + surface upgrade prompt.
        setCan4k(false);
        setOutputScale('1080p');
        toast.error(serverMsg || '4K export requires the Studio or Agency plan');
      } else {
        toast.error(serverMsg || error.message || 'Failed to start MP4 export');
      }
    }
  };

  // Force re-export: re-renders every slide from scratch, ignoring per-slide
  // and assembly caches. Overwrites the cached mp4s so the new output becomes
  // the baseline. Use when the cache is known-stale (e.g. renderer changes
  // that don't bump the slide hash).
  const handleForceExportMp4 = async () => {
    if (!allSlidesReady) {
      toast.warning('Please wait for all slides to be generated before exporting');
      return;
    }

    ensureNotificationPermission();

    try {
      await exportAll.startExportAll(conversationId, {
        force: true,
        removeWatermark: canRemoveWatermark && removeWatermark,
        outputScale: can4k && outputScale === '4k' ? '4k' : '1080p',
      });
      toast.info('Re-exporting all slides from scratch — this will take longer than a cached export.');
    } catch (error: any) {
      console.error('Failed to start MP4 re-export:', error);
      const serverMsg = error?.response?.data?.error;
      const code = error?.response?.data?.code;
      if (code === 'FOUR_K_NOT_ALLOWED') {
        setCan4k(false);
        setOutputScale('1080p');
        toast.error(serverMsg || '4K export requires the Studio or Agency plan');
      } else {
        toast.error(serverMsg || error.message || 'Failed to start MP4 re-export');
      }
    }
  };

  // Start PDF export
  const handleExportPdf = async () => {
    if (!allSlidesReady) {
      toast.warning('Please wait for all slides to be generated before exporting');
      return;
    }

    ensureNotificationPermission();

    try {
      await pdfExport.startPdfExport(conversationId);
    } catch (error: any) {
      console.error('Failed to start PDF export:', error);
      toast.error(error.message || 'Failed to start PDF export');
    }
  };

  // Download PDF
  const handleDownloadPdf = async () => {
    if (!pdfExport.jobId) return;
    try {
      const result = await videoExportAPI.getDownloadUrl(pdfExport.jobId);
      if (result.success && result.downloadUrl) {
        window.open(result.downloadUrl, '_blank');
      }
    } catch (error: any) {
      console.error('Failed to get PDF download URL:', error);
      toast.error(error.message || 'Failed to get PDF download URL');
    }
  };

  // Unpublish (revoke) a specific link
  const handleUnpublish = async () => {
    if (!unpublishTargetId) return;

    try {
      setUnpublishingId(unpublishTargetId);
      await videoShareAPI.unpublish(conversationId, unpublishTargetId);

      toast.success('Link revoked');
      setShowUnpublishDialog(false);
      setUnpublishTargetId(null);

      // Refresh status
      await fetchPublishStatus();
    } catch (error: any) {
      console.error('Failed to unpublish video:', error);
      toast.error(error.message || 'Failed to unpublish video');
    } finally {
      setUnpublishingId(null);
    }
  };

  // Copy URL to clipboard
  const handleCopy = async (url: string, key: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(key);
      toast.success('Copied!');
      setTimeout(() => setCopied(null), 2000);
    } catch (error) {
      toast.error('Failed to copy to clipboard');
    }
  };

  // Format view count
  const formatViewCount = (count: number): string => {
    if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
    if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
    return count.toString();
  };

  // Generate embed code. Prefer the direct baked CDN bundle (fastest, no redirect);
  // fall back to /embed when not baked yet. The `allow` grants fullscreen/autoplay to
  // the CDN origin too, so fullscreen still works across the /embed→CDN redirect.
  const getEmbedCode = (link: VideoShareLinkInfo): string => {
    const src = link.staticBundleUrl ?? link.embedUrl;
    return `<iframe src="${src}" width="${resolution.width}" height="${resolution.height}" frameborder="0" allow="autoplay 'src' https://play.4klabs.ai; fullscreen 'src' https://play.4klabs.ai" allowfullscreen></iframe>`;
  };

  // Format relative time
  const formatRelativeTime = (date: Date | string): string => {
    const d = new Date(date);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHrs = Math.floor(diffMins / 60);
    if (diffHrs < 24) return `${diffHrs}h ago`;
    const diffDays = Math.floor(diffHrs / 24);
    if (diffDays < 30) return `${diffDays}d ago`;
    return d.toLocaleDateString();
  };

  // Get slide title from info array
  const getSlideTitle = (slideExport: SlideExportStatus): string => {
    const match = slidesInfo.find(s => s.id === slideExport.outlineSlideId);
    return match?.title || slideExport.slideIdentifier;
  };

  // Render the export status icon per slide
  const renderSlideStatusIcon = (slide: SlideExportStatus) => {
    if (slide.skipped) {
      return <SkipForward className="h-3.5 w-3.5 text-primary shrink-0" />;
    }
    switch (slide.status) {
      case 'COMPLETED':
        return <CheckCircle2 className="h-3.5 w-3.5 text-green-500 shrink-0" />;
      case 'FAILED':
        return <XCircle className="h-3.5 w-3.5 text-red-500 shrink-0" />;
      case 'PROCESSING':
        return <Loader2 className="h-3.5 w-3.5 text-primary animate-spin shrink-0" />;
      default:
        return <div className="h-3.5 w-3.5 rounded-full border-2 border-muted-foreground/30 shrink-0" />;
    }
  };

  // Whether export is actively running
  const isExportActive = exportAll.isExporting || pdfExport.isExporting;
  // Whether we have export results to show (active, completed, or failed)
  const hasExportState = exportAll.phase !== 'idle';
  // Whether the assembled video is outdated
  const isStale = exportAll.isStale;
  // Whether PDF export has results to show
  const hasPdfState = pdfExport.phase !== 'idle';

  // Handle download of assembled MP4
  const handleDownloadMp4 = async () => {
    if (!exportAll.assemblyJobId) return;
    try {
      const result = await videoExportAPI.getDownloadUrl(exportAll.assemblyJobId);
      if (result.success && result.downloadUrl) {
        window.open(result.downloadUrl, '_blank');
      }
    } catch (error: any) {
      console.error('Failed to get download URL:', error);
      toast.error(error.message || 'Failed to get download URL');
    }
  };

  // Phase display label
  const getPhaseLabel = (): string => {
    switch (exportAll.phase) {
      case 'submitting': return 'Preparing export...';
      case 'exporting': return 'Rendering slides...';
      case 'assembling': return 'Combining slides...';
      case 'rendering': return 'Rendering video...';
      case 'completed': return 'Export Complete';
      case 'failed': return 'Export Failed';
      case 'cancelled': return 'Export Cancelled';
      default: return '';
    }
  };

  const renderBuyCredits = (action: string) => (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        useUpgradeModal.getState().open({ code: 'OUT_OF_CREDITS', action });
      }}
      className="shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium text-primary hover:bg-primary/10"
    >
      Buy credits
    </button>
  );

  // Resolution preset selector. Two rows, radio-style. Default 1080p; 4K is
  // unlocked by any paid (credits) entitlement. Locked rows offer
  // Buy credits — server independently rejects 4K from non-entitled users.
  const renderResolutionToggle = () => {
    const fourKLocked = !can4k;
    const renderRow = (
      key: '1080p' | '4k',
      label: string,
      sublabel: string,
      locked: boolean,
      lockHint: string,
    ) => {
      const selected = outputScale === key;
      return (
        <div key={key} className="flex items-center gap-1">
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (locked) return;
              setOutputScale(key);
            }}
            className={cn(
              'flex items-center gap-2 flex-1 min-w-0 px-2 py-1.5 text-sm text-left rounded-sm transition-colors',
              locked
                ? 'cursor-not-allowed text-muted-foreground/80 hover:bg-muted/40'
                : 'hover:bg-muted cursor-pointer'
            )}
            title={locked ? lockHint : undefined}
            aria-pressed={selected}
          >
            <span
              className={cn(
                'inline-flex items-center justify-center h-4 w-4 rounded-full border shrink-0',
                selected ? 'border-primary' : 'border-muted-foreground/40'
              )}
              aria-hidden="true"
            >
              {selected && <span className="h-2 w-2 rounded-full bg-primary" />}
            </span>
            <span className="flex-1 min-w-0 flex flex-col">
              <span className="flex items-center gap-1.5">
                <span>{label}</span>
                {locked && <Lock className="h-3 w-3 opacity-60" />}
              </span>
              <span className="text-[10px] text-muted-foreground/80 leading-tight">{sublabel}</span>
            </span>
          </button>
          {locked && renderBuyCredits('export in 4K')}
        </div>
      );
    };
    return (
      <div className="flex flex-col">
        {renderRow('1080p', '1080p', 'Full HD — fastest, best for review', false, '')}
        {renderRow(
          '4k',
          '4K (3840 × 2160)',
          'Best for YouTube / TikTok / LinkedIn upload',
          fourKLocked,
          'Buy credits to export in 4K',
        )}
      </div>
    );
  };

  // Watermark-removal toggle. Entitlement-gated: a paid (credits) account can
  // flip it off; every other account sees a locked row with an upsell hint. The server
  // independently coerces the request against the entitlement, so this is a
  // hint, not the gate.
  const renderWatermarkToggle = () => {
    const locked = !canRemoveWatermark;
    const checked = !locked && removeWatermark;
    return (
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (locked) return;
            setRemoveWatermark(v => !v);
          }}
          className={cn(
            'flex items-center gap-2 flex-1 min-w-0 px-2 py-1.5 text-sm text-left rounded-sm transition-colors',
            locked
              ? 'cursor-not-allowed text-muted-foreground/80 hover:bg-muted/40'
              : 'hover:bg-muted cursor-pointer'
          )}
          title={locked ? 'Buy credits to remove the watermark' : undefined}
        >
          <span
            className={cn(
              'inline-flex items-center justify-center h-4 w-4 rounded border shrink-0',
              checked ? 'bg-primary border-primary text-primary-foreground' : 'border-muted-foreground/40'
            )}
            aria-hidden="true"
          >
            {checked && <Check className="h-3 w-3" />}
          </span>
          <span className="flex-1 min-w-0 flex items-center gap-1.5">
            <span>Remove "4K Labs" watermark</span>
            {locked && <Lock className="h-3 w-3 opacity-60" />}
          </span>
        </button>
        {locked && renderBuyCredits('remove the watermark')}
      </div>
    );
  };

  // Inline export progress section for the dropdown
  const renderExportProgress = () => {
    if (!hasExportState) return null;

    return (
      <>
        <DropdownMenuSeparator />
        <div className="p-2">
          {/* Header row */}
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1.5">
              <Film className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="text-xs font-medium text-muted-foreground">
                {getPhaseLabel()}
              </span>
            </div>
            {isExportActive && (
              <span className="text-xs text-muted-foreground">{exportAll.overallProgress}%</span>
            )}
          </div>

          {/* Overall progress bar (only while active) */}
          {isExportActive && (
            <Progress value={exportAll.overallProgress} className="h-1.5 mb-2" />
          )}

          {/* Per-slide list (show during exporting) */}
          {exportAll.phase === 'exporting' && exportAll.slides.length > 0 && (
            <div className="space-y-1.5 max-h-[200px] overflow-y-auto">
              {exportAll.slides.map((slide, idx) => (
                <div key={slide.jobId || idx} className="flex items-center gap-2">
                  {renderSlideStatusIcon(slide)}
                  <span className="text-xs truncate flex-1 min-w-0">
                    {getSlideTitle(slide)}
                  </span>
                  <span className="text-[10px] text-muted-foreground shrink-0">
                    {slide.skipped
                      ? 'cached'
                      : slide.status === 'COMPLETED'
                        ? 'done'
                        : slide.status === 'FAILED'
                          ? 'failed'
                          : slide.status === 'PROCESSING'
                            ? `${slide.progress}%`
                            : 'queued'}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Assembly status */}
          {exportAll.phase === 'assembling' && (
            <div className="flex items-center gap-2 mt-1">
              <Loader2 className="h-3.5 w-3.5 text-primary animate-spin shrink-0" />
              <span className="text-xs text-muted-foreground">
                Merging video with transitions and audio...
              </span>
            </div>
          )}

          {/* Program render: one job for the whole video */}
          {exportAll.phase === 'rendering' && (
            <div className="flex items-center gap-2 mt-1">
              <Loader2 className="h-3.5 w-3.5 text-primary animate-spin shrink-0" />
              <span className="text-xs text-muted-foreground truncate">
                {exportAll.currentOperation || 'Rendering video...'}
              </span>
            </div>
          )}

          {exportAll.phase === 'failed' && exportAll.error && (
            <p className="text-xs text-red-600 dark:text-red-400 mt-1 break-words">{exportAll.error}</p>
          )}

          {/* Cancel button while active */}
          {exportAll.canCancel && (
            <button
              onClick={async (e) => {
                e.preventDefault();
                e.stopPropagation();
                try {
                  await exportAll.cancel();
                  toast.info('Export cancelled');
                } catch (err: any) {
                  toast.error(err?.message || 'Failed to cancel export');
                }
              }}
              className="flex items-center justify-center gap-1.5 w-full mt-1.5 px-2 py-1.5 text-xs font-medium rounded bg-red-500/10 text-red-600 dark:text-red-400 hover:bg-red-500/20 transition-colors"
            >
              Cancel export
            </button>
          )}

          {/* Download button when complete */}
          {exportAll.phase === 'completed' && exportAll.assemblyJobId && (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleDownloadMp4();
              }}
              className="flex items-center gap-1.5 w-full mt-1.5 px-2 py-1.5 text-xs font-medium rounded bg-green-500/10 text-green-600 dark:text-green-400 hover:bg-green-500/20 transition-colors"
            >
              <Download className="h-3.5 w-3.5" />
              Download MP4
            </button>
          )}

          {/* Clear results button when done */}
          {(exportAll.phase === 'completed' || exportAll.phase === 'failed' || exportAll.phase === 'cancelled') && (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                exportAll.reset();
              }}
              className="text-[10px] text-muted-foreground hover:text-foreground mt-1.5 underline"
            >
              Dismiss
            </button>
          )}
        </div>
      </>
    );
  };

  // Inline PDF export progress section for the dropdown
  const renderPdfExportProgress = () => {
    if (!hasPdfState) return null;

    return (
      <>
        <DropdownMenuSeparator />
        <div className="p-2">
          {/* Header row */}
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1.5">
              <FileText className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="text-xs font-medium text-muted-foreground">
                {pdfExport.phase === 'submitting' ? 'Preparing PDF...'
                  : pdfExport.phase === 'exporting' ? 'Generating PDF...'
                  : pdfExport.phase === 'completed' ? 'PDF Ready'
                  : pdfExport.phase === 'failed' ? 'PDF Export Failed'
                  : ''}
              </span>
            </div>
            {pdfExport.isExporting && (
              <span className="text-xs text-muted-foreground">{pdfExport.progress}%</span>
            )}
          </div>

          {/* Progress bar (only while active) */}
          {pdfExport.isExporting && (
            <Progress value={pdfExport.progress} className="h-1.5 mb-2" />
          )}

          {/* Current operation */}
          {pdfExport.isExporting && pdfExport.currentOperation && (
            <div className="flex items-center gap-2 mt-1">
              <Loader2 className="h-3.5 w-3.5 text-primary animate-spin shrink-0" />
              <span className="text-xs text-muted-foreground truncate">
                {pdfExport.currentOperation}
              </span>
            </div>
          )}

          {/* Download button when complete */}
          {pdfExport.phase === 'completed' && pdfExport.jobId && (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleDownloadPdf();
              }}
              className="flex items-center gap-1.5 w-full mt-1.5 px-2 py-1.5 text-xs font-medium rounded bg-green-500/10 text-green-600 dark:text-green-400 hover:bg-green-500/20 transition-colors"
            >
              <Download className="h-3.5 w-3.5" />
              Download PDF
            </button>
          )}

          {/* Clear results button when done */}
          {(pdfExport.phase === 'completed' || pdfExport.phase === 'failed') && (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                pdfExport.reset();
              }}
              className="text-[10px] text-muted-foreground hover:text-foreground mt-1.5 underline"
            >
              Dismiss
            </button>
          )}
        </div>
      </>
    );
  };

  // Render a single share link card within the dropdown
  const renderShareLinkCard = (link: VideoShareLinkInfo) => {
    const isUpdating = updatingLinkId === link.id;
    const shareKey = `share-${link.id}`;
    // Hand out the static player when it is baked — it is the only url that unfurls
    // a preview in Slack/LinkedIn/WhatsApp. Reviewer links stay on shareUrl below,
    // since ?review=true is implemented in the web-ui route only.
    const copyUrl = link.playUrl || link.shareUrl;
    const embedKey = `embed-${link.id}`;

    return (
      <div key={link.id} className="p-2 border-b border-border/50 last:border-b-0">
        {/* Outdated badge — fires when one or more pinned slide versions diverge
            from the current workspace versions. "Update to Latest" below
            re-pins to current. */}
        {link.isOutdated && (
          <div className="flex items-center gap-1 px-1.5 py-1 mb-1.5 rounded bg-amber-500/10 border border-amber-500/20">
            <AlertTriangle className="h-3 w-3 text-amber-500 shrink-0" />
            <span className="text-[11px] text-amber-700 dark:text-amber-300">
              {link.outdatedSlideCount} slide{link.outdatedSlideCount === 1 ? '' : 's'} outdated — click "Update to Latest" to re-pin
            </span>
          </div>
        )}
        {/* URL row */}
        <div className="flex items-center gap-2 mb-1.5">
          <div className="flex-1 bg-muted/50 rounded px-2 py-1.5 text-xs truncate font-mono">
            {copyUrl}
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0 shrink-0"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleCopy(copyUrl, shareKey);
            }}
          >
            {copied === shareKey ? (
              <Check className="h-3.5 w-3.5 text-green-500" />
            ) : (
              <Copy className="h-3.5 w-3.5" />
            )}
          </Button>
        </div>

        {/* Reviewer access — toggle + dedicated review-link copy.                 */}
        {/* Off by default (set on the server). Toggle is reversible at any time. */}
        <ReviewerAccessRow
          link={link}
          onToggle={(next) => handleToggleAllowReviews(link.id, next)}
          // Deliberately shareUrl, not copyUrl: ?review=true is implemented in the
          // web-ui route, and does nothing on the static player url.
          onCopyReviewLink={() => handleCopy(`${link.shareUrl}?review=true`, `review-${link.id}`)}
          copied={copied === `review-${link.id}`}
        />

        <ViewerLinkRow
          url={copyUrl}
          linkKey={link.id}
          onCopy={(viewerUrl) => handleCopy(viewerUrl, `viewer-${link.id}`)}
          copied={copied === `viewer-${link.id}`}
        />

        {/* Meta row: views, created/updated, actions */}
        <div className="flex items-center justify-between text-[10px] text-muted-foreground mb-1.5">
          <div className="flex items-center gap-2">
            {link.viewCount > 0 && (
              <span className="flex items-center gap-0.5">
                <Eye className="h-3 w-3" /> {formatViewCount(link.viewCount)}
              </span>
            )}
            <span>
              {link.updatedAt
                ? `Updated ${formatRelativeTime(link.updatedAt)}`
                : `Created ${formatRelativeTime(link.createdAt)}`
              }
            </span>
          </div>
          <span>{link.slideCount} slides</span>
        </div>

        {/* Action buttons row */}
        <div className="flex items-center gap-1">
          {/* Update to latest */}
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleUpdateLink(link.id);
            }}
            disabled={isUpdating || !allSlidesReady}
            className="flex items-center gap-1 px-2 py-1 text-[11px] rounded bg-primary/10 text-primary dark:text-accent-foreground hover:bg-primary/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isUpdating ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <RefreshCw className="h-3 w-3" />
            )}
            Update to Latest
          </button>

          {/* Open in new tab */}
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              window.open(copyUrl, '_blank');
            }}
            className="flex items-center gap-1 px-2 py-1 text-[11px] rounded hover:bg-muted transition-colors text-muted-foreground"
          >
            <ExternalLink className="h-3 w-3" />
          </button>

          {/* Copy embed code — direct CDN bundle when baked (⚡), else /embed */}
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleCopy(getEmbedCode(link), embedKey);
            }}
            title={link.staticBundleUrl ? 'Copy embed code (⚡ optimized edge player)' : 'Copy embed code'}
            className="flex items-center gap-1 px-2 py-1 text-[11px] rounded hover:bg-muted transition-colors text-muted-foreground"
          >
            {copied === embedKey ? (
              <Check className="h-3 w-3 text-green-500" />
            ) : (
              <Code className="h-3 w-3" />
            )}
            {link.staticBundleUrl && copied !== embedKey && <Zap className="h-3 w-3 text-amber-500" />}
          </button>

          {/* Analytics for this link */}
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              window.open(`/analytics/${link.shareToken}`, '_blank');
            }}
            title="Analytics for this link"
            className="flex items-center gap-1 px-2 py-1 text-[11px] rounded hover:bg-muted transition-colors text-muted-foreground"
          >
            <BarChart3 className="h-3 w-3" />
          </button>

          {/* Revoke */}
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setUnpublishTargetId(link.id);
              setShowUnpublishDialog(true);
            }}
            className="flex items-center gap-1 px-2 py-1 text-[11px] rounded hover:bg-destructive/10 transition-colors text-destructive ml-auto"
          >
            <Trash2 className="h-3 w-3" />
          </button>
        </div>
      </div>
    );
  };

  if (isLoading || (exportAll.isRestoring && exportAll.phase === 'idle')) {
    return (
      <Button
        variant="ghost"
        size={compact ? 'sm' : 'default'}
        disabled
        className={cn('gap-2', className)}
      >
        <Loader2 className="h-4 w-4 animate-spin" />
        {!compact && <span>Loading...</span>}
      </Button>
    );
  }

  // Not published — show dropdown with "Generate Link" and export options
  if (!isPublished || activeLinks.length === 0) {
    return (
      <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            disabled={isPublishing || (!allSlidesReady && !isExportActive && !hasExportState) || (slideCount === 0 && !isExportActive && !hasExportState)}
            title={isExportActive || hasExportState ? undefined
              : slideCount === 0 ? 'Add slides to publish this video'
                : !allSlidesReady ? 'Publish opens once every slide has finished building'
                  : undefined}
            size={compact ? 'sm' : 'default'}
            className={cn(
              'gap-2',
              b && 'h-8 rounded-[var(--b-radius-control)] bg-b-action px-3 text-b-control text-b-on-action hover:bg-b-action hover:opacity-90',
              !allSlidesReady && !isExportActive && !hasExportState && 'opacity-50',
              className
            )}
          >
            {isPublishing || isExportActive ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : exportAll.phase === 'completed' && isStale ? (
              <AlertTriangle className="h-4 w-4 text-amber-500" />
            ) : exportAll.phase === 'completed' ? (
              <Download className="h-4 w-4" />
            ) : (
              <Globe className="h-4 w-4" />
            )}
            {(!compact || !isExportActive) && (
              <span>
                {isPublishing
                  ? 'Publishing...'
                  : exportAll.phase === 'assembling'
                    ? `Combining ${exportAll.overallProgress}%`
                    : isExportActive
                      ? `Exporting ${exportAll.overallProgress}%`
                      : exportAll.phase === 'completed' && isStale
                        ? 'MP4 Outdated'
                        : exportAll.phase === 'completed'
                          ? 'MP4 Ready'
                          : 'Publish'}
              </span>
            )}
            {compact && isExportActive && (
              <span className="text-[10px]">{exportAll.overallProgress}%</span>
            )}
            <ChevronDown className="h-3 w-3 opacity-50" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" {...(b ? { 'data-b-surface': '' } : {})} className={b ? B_MENU : 'w-72'}>
          {hasBlog && <ShareTabStrip activeTab={shareTab} onChange={setShareTab} />}
          {(!hasBlog || shareTab === 'video') && (
            <>
              {/* Detailed change badge — shows what slide versions / audio / volume
                  have moved since the last successful export. Renders nothing when
                  clean or no prior export exists. */}
              {exportDiff.diff && <ExportChangeBadge diff={exportDiff.diff} />}
              {/* Download button at top when assembly is complete */}
              {exportAll.phase === 'completed' && exportAll.assemblyJobId && (
                <>
                  <DropdownMenuItem onClick={handleDownloadMp4}>
                    <Download className="h-4 w-4 mr-2" />
                    {isStale ? 'Download MP4 (outdated)' : 'Download MP4'}
                  </DropdownMenuItem>
                  {isStale && (
                    <DropdownMenuItem onClick={handleExportMp4} disabled={isExportActive || !allSlidesReady}>
                      <Film className="h-4 w-4 mr-2" />
                      Re-export with latest
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                </>
              )}
              {b ? (
                <DropdownMenuItem onClick={handlePublish} disabled={isPublishing} className={B_PRIMARY}>
                  Generate link
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem onClick={handlePublish} disabled={isPublishing}>
                  <Globe className="h-4 w-4 mr-2" />
                  Generate Link
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <div className="px-1 py-0.5">{renderResolutionToggle()}</div>
              <div className="px-1 py-0.5">{renderWatermarkToggle()}</div>
              <DropdownMenuItem onClick={handleExportMp4} disabled={isExportActive || (exportAll.phase === 'completed' && !isStale) || !allSlidesReady}>
                <Film className="h-4 w-4 mr-2" />
                Export to MP4
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleForceExportMp4} disabled={isExportActive || !allSlidesReady}>
                <RotateCcw className="h-4 w-4 mr-2" />
                Re-export (skip cache)
              </DropdownMenuItem>
              {pdfExport.phase === 'completed' && pdfExport.jobId ? (
                <DropdownMenuItem onClick={handleDownloadPdf}>
                  <Download className="h-4 w-4 mr-2" />
                  Download PDF
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem onClick={handleExportPdf} disabled={pdfExport.isExporting || !allSlidesReady}>
                  <FileText className="h-4 w-4 mr-2" />
                  Export to PDF
                </DropdownMenuItem>
              )}

              {/* Inline export progress */}
              {renderExportProgress()}
              {renderPdfExportProgress()}
            </>
          )}

          {/* Blog tab content */}
          {hasBlog && shareTab === 'blog' && (
            <BlogShareSection conversationId={conversationId} />
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  // Published — show all active links with per-link management + export options
  const totalViews = activeLinks.reduce((sum, l) => sum + l.viewCount, 0);

  return (
    <>
      <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size={compact ? 'sm' : 'default'}
            aria-label={compact ? 'Published links' : undefined}
            className={cn(
              'gap-2',
              isExportActive
                ? 'border-primary/30 bg-primary/10 text-primary dark:text-accent-foreground hover:bg-primary/20'
                : 'border-green-500/30 bg-green-500/10 text-green-600 dark:text-green-400 hover:bg-green-500/20',
              className
            )}
          >
            {isExportActive ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Globe className="h-4 w-4" />
            )}
            {!compact && (
              <span>
                {exportAll.phase === 'assembling'
                  ? `Combining ${exportAll.overallProgress}%`
                  : isExportActive
                    ? `Exporting ${exportAll.overallProgress}%`
                    : activeLinks.length === 1
                      ? 'Published'
                      : `${activeLinks.length} Links`}
              </span>
            )}
            {compact && isExportActive && (
              <span className="text-[10px]">{exportAll.overallProgress}%</span>
            )}
            {!isExportActive && totalViews > 0 && (
              <span className="flex items-center gap-1 text-xs opacity-70">
                <Eye className="h-3 w-3" />
                {formatViewCount(totalViews)}
              </span>
            )}
            <ChevronDown className="h-3 w-3 opacity-50" />
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end" {...(b ? { 'data-b-surface': '' } : {})} className={b ? B_MENU : 'w-80'}>
          {hasBlog && <ShareTabStrip activeTab={shareTab} onChange={setShareTab} />}
          {(!hasBlog || shareTab === 'video') && (
            <>
              {/* Detailed "what changed since last export" panel */}
              {exportDiff.diff && <ExportChangeBadge diff={exportDiff.diff} />}

              {/* Published links header */}
              <div className="px-2 py-1.5 flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <Link2 className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-xs font-medium text-muted-foreground">
                    Published Links ({activeLinks.length})
                  </span>
                </div>
              </div>

              {/* Share link cards */}
              <div className="max-h-[280px] overflow-y-auto">
                {activeLinks.map(link => renderShareLinkCard(link))}
              </div>

              <DropdownMenuSeparator />

              {/* New link button */}
              <DropdownMenuItem onClick={handlePublish} disabled={isPublishing || !allSlidesReady}>
                {isPublishing ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Plus className="h-4 w-4 mr-2" />
                )}
                New Link
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => window.open(`/analytics?video=${conversationId}`, '_blank')}>
                <BarChart3 className="h-4 w-4 mr-2" />
                Analytics
              </DropdownMenuItem>

              {/* Export options */}
              <DropdownMenuSeparator />
              <div className="px-1 py-0.5">{renderResolutionToggle()}</div>
              <div className="px-1 py-0.5">{renderWatermarkToggle()}</div>
              {/* Download MP4 (shown when assembly is complete) */}
              {exportAll.phase === 'completed' && exportAll.assemblyJobId ? (
                <>
                  <DropdownMenuItem onClick={handleDownloadMp4}>
                    <Download className="h-4 w-4 mr-2" />
                    {isStale ? 'Download MP4 (outdated)' : 'Download MP4'}
                  </DropdownMenuItem>
                  {isStale && (
                    <DropdownMenuItem onClick={handleExportMp4} disabled={isExportActive || !allSlidesReady}>
                      <Film className="h-4 w-4 mr-2" />
                      Re-export with latest
                    </DropdownMenuItem>
                  )}
                </>
              ) : (
                <DropdownMenuItem onClick={handleExportMp4} disabled={isExportActive || !allSlidesReady}>
                  <Film className="h-4 w-4 mr-2" />
                  Export to MP4
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={handleForceExportMp4} disabled={isExportActive || !allSlidesReady}>
                <RotateCcw className="h-4 w-4 mr-2" />
                Re-export (skip cache)
              </DropdownMenuItem>

              {/* PDF export */}
              {pdfExport.phase === 'completed' && pdfExport.jobId ? (
                <DropdownMenuItem onClick={handleDownloadPdf}>
                  <Download className="h-4 w-4 mr-2" />
                  Download PDF
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem onClick={handleExportPdf} disabled={pdfExport.isExporting || !allSlidesReady}>
                  <FileText className="h-4 w-4 mr-2" />
                  Export to PDF
                </DropdownMenuItem>
              )}

              {/* Inline export progress */}
              {renderExportProgress()}
              {renderPdfExportProgress()}
            </>
          )}

          {/* Blog tab content */}
          {hasBlog && shareTab === 'blog' && (
            <BlogShareSection conversationId={conversationId} />
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Unpublish Confirmation Dialog */}
      <Dialog open={showUnpublishDialog} onOpenChange={setShowUnpublishDialog}>
        <DialogContent {...(b ? { 'data-b-surface': '' } : {})} className={b ? B_DIALOG : undefined}>
          <DialogHeader>
            <DialogTitle>Revoke Share Link?</DialogTitle>
            <DialogDescription>
              This will deactivate this share link. Anyone with the link will no longer
              be able to view this video. Other published links will not be affected.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowUnpublishDialog(false);
                setUnpublishTargetId(null);
              }}
              disabled={!!unpublishingId}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleUnpublish}
              disabled={!!unpublishingId}
            >
              {unpublishingId ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Revoking...
                </>
              ) : (
                'Revoke Link'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
